// Service-rate admin hooks. The time-entries module already exposes
// useServiceRates(clientId) for the dashboard picker; this file adds
// the create/update mutations + the include-inactive list option for
// the /service-rates admin page.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CopyRatesToGlobalResponse,
  EngagementRatesPreviewResponse,
  ImportEngagementRatesResponse,
  MergeRatesResponse,
  ServiceRate,
  ServiceRatesListResponse,
} from '@stfors/shared-types';
import { api } from '../client';

export interface ServiceRatesAdminFilters {
  include_inactive?: boolean;
  client_id?: number;
  // When set, the API returns global + the project's client overrides + the
  // project's own overrides, ranked most-specific-first (Project → Client →
  // Global) so the page can compute the effective sheet.
  project_id?: number;
}

export function useServiceRatesAdmin(filters: ServiceRatesAdminFilters = {}) {
  const params = new URLSearchParams();
  if (filters.include_inactive) params.set('include_inactive', 'true');
  if (filters.client_id !== undefined) params.set('client_id', String(filters.client_id));
  if (filters.project_id !== undefined) params.set('project_id', String(filters.project_id));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/service-rates?${qs}` : '/api/service-rates';
  return useQuery({
    queryKey: ['service-rates', { ...filters }],
    queryFn: ({ signal }) => api.get<ServiceRatesListResponse>(url, signal),
  });
}

export interface CreateServiceRateInput {
  name: string;
  rate_type: 'hourly' | 'flat_fee';
  amount: string;
  description?: string;
  client_override_for?: number | null;
  project_override_for?: number | null;
  /** Entries on this rate are device sales (0059). Flat-fee rates only. */
  is_product_sale?: boolean;
}

export function useCreateServiceRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServiceRateInput) =>
      api.post<{ service_rate: ServiceRate }>('/api/service-rates', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-rates'] });
    },
  });
}

export interface UpdateServiceRateInput {
  id: number;
  name?: string;
  rate_type?: 'hourly' | 'flat_fee';
  amount?: string;
  description?: string | null;
  is_active?: boolean;
  /** Applies to every tier of the rate's name, not just this row (0059). */
  is_product_sale?: boolean;
}

export function useUpdateServiceRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateServiceRateInput) =>
      api.patch<{ service_rate: ServiceRate }>(`/api/service-rates/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-rates'] });
    },
  });
}

// Soft-delete a rate. Used to "clear" a client/project override so the rate
// reverts to its inherited level.
export function useDeleteServiceRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/service-rates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-rates'] });
    },
  });
}

// "Make Global match exactly": promote a scope's effective rate sheet into the
// Global defaults. Provide exactly one of from_client_id / from_project_id.
export interface CopyRatesToGlobalInput {
  from_client_id?: number;
  from_project_id?: number;
}

export function useCopyRatesToGlobal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CopyRatesToGlobalInput) =>
      api.post<CopyRatesToGlobalResponse>('/api/service-rates/copy-to-global', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-rates'] });
    },
  });
}

// Parsed engagement-letter rates (Exhibit B) for a project, mapped to
// service-rate candidates — powers the "Import from engagement letter" prompt
// on the project scope of the Rates page.
export function useEngagementRateCandidates(projectId?: number) {
  return useQuery({
    queryKey: ['engagement-rate-candidates', { project_id: projectId ?? null }],
    enabled: projectId !== undefined,
    queryFn: ({ signal }) =>
      api.get<EngagementRatesPreviewResponse>(
        `/api/service-rates/engagement-rates?project_id=${projectId}`,
        signal,
      ),
  });
}

// Promote a project's engagement-letter rate sheet into project-scoped rates.
export function useImportEngagementRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: number) =>
      api.post<ImportEngagementRatesResponse>('/api/service-rates/import-from-engagement', {
        project_id: projectId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-rates'] });
    },
  });
}

// Merge a differently-worded rate into a canonical one (drag-to-merge).
// amount = null means "inherit" (delete the source so it falls back to the
// target name's less-specific level); a value means override at the source's
// scope under the target name.
export interface MergeRatesInput {
  source_id: number;
  target_name: string;
  amount: string | null;
}

export function useMergeRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MergeRatesInput) =>
      api.post<MergeRatesResponse>('/api/service-rates/merge', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-rates'] });
    },
  });
}
